import { reactive } from 'vue'
import type egresses from '@/backend/egresses'
import type ingresses from '@/backend/ingresses'
import type { ResourceLogging } from '@/backend/ingresses.types'
import type { useResourcesStore } from '@/stores/resources/resources-store'
import type {
  LoggingCeilings,
  LoggingFilters,
  LoggingRow,
  LoggingViewContext,
  ResourceFamily,
} from './logging-view-context'


export interface LoggingServices {
  egressesService: ReturnType<typeof egresses>
  ingressesService: ReturnType<typeof ingresses>
  registryStore: { get(key: string): Promise<any> }
  resourcesStore: ReturnType<typeof useResourcesStore>
}

export interface LoggingPermissions {
  ingressWrite: boolean
  egressWrite: boolean
}

export class LoggingViewContextImpl implements LoggingViewContext {
  readonly rows = reactive<LoggingRow[]>([])
  readonly ceilings = reactive<LoggingCeilings>({})

  readonly workState = reactive({
    isLoading: false,
    isSaving: false,
    error: null as string | null,
  })

  readonly filters = reactive<LoggingFilters>({
    family: 'all',
    overriddenOnly: false,
    search: '',
  })


  private readonly egressesService: ReturnType<typeof egresses>
  private readonly ingressesService: ReturnType<typeof ingresses>
  private readonly registryStore: { get(key: string): Promise<any> }
  private readonly resourcesStore: ReturnType<typeof useResourcesStore>

  constructor(
    services: LoggingServices,
    private readonly permissions: LoggingPermissions,
  ) {
    this.egressesService = services.egressesService
    this.ingressesService = services.ingressesService
    this.registryStore = services.registryStore
    this.resourcesStore = services.resourcesStore
  }

  get ceilingsUnavailable(): boolean {
    return Object.keys(this.ceilings).length === 0
  }

  get filteredRows(): LoggingRow[] {
    const term = this.filters.search.trim().toLowerCase()

    return this.rows.filter((row) => {
      if (this.filters.family !== 'all' && row.family !== this.filters.family) {
        return false
      }

      if (this.filters.overriddenOnly && !hasOverride(row)) {
        return false
      }


      return !term || row.resourceId.toLowerCase().includes(term)
    })
  }

  /**
   * Projects the cached resource lists into grid rows, and reads the ceilings alongside.
   *
   * The lists come from the `resources` store rather than being fetched here: they are tenant-wide
   * and single-instance, so several views mounting together should share one round trip.
   */
  async refresh(): Promise<void> {
    this.workState.isLoading = true
    this.workState.error = null


    await Promise.all([
      this.resourcesStore.load({
        egressesService: this.egressesService,
        ingressesService: this.ingressesService,
      }),
      this.loadCeilings(),
    ])

    const collected: LoggingRow[] = [
      ...this.resourcesStore.ingresses.map((ingress) => ({
        resourceId: ingress.ingressId,
        family: 'ingress' as ResourceFamily,
        canWrite: this.permissions.ingressWrite,
        logLevel: ingress.logLevel ?? null,
        logRetentionDays: ingress.logRetentionDays ?? null,
        logFullRetentionDays: ingress.logFullRetentionDays ?? null,
      })),
      ...this.resourcesStore.egresses.map((egress) => ({
        resourceId: egress.egressId,
        family: 'egress' as ResourceFamily,
        canWrite: this.permissions.egressWrite,
        logLevel: egress.logLevel ?? null,
        logRetentionDays: egress.logRetentionDays ?? null,
        logFullRetentionDays: egress.logFullRetentionDays ?? null,
      })),
    ].sort(
      (a, b) => a.family.localeCompare(b.family) || a.resourceId.localeCompare(b.resourceId),
    )

    this.rows.splice(0, this.rows.length, ...collected)

    this.workState.isLoading = false
    this.workState.error = this.resourcesStore.error
  }


  /**
   * Degraded rather than fatal when unreadable: the API validates retention regardless, and its
   * `400 INVALID_ARGUMENT` names the limit. Losing the hint line and the client-side bounds is
   * preferable to a blank tab.
   */
  private async loadCeilings(): Promise<void> {
    try {
      const [defaultLevel, metadataHours, maxMetadataHours, fullHours, maxFullHours] = await Promise.all([
        this.registryStore.get('system/trafficLogging/defaultLevel'),
        this.registryStore.get('system/trafficLogging/metadataHours'),
        this.registryStore.get('system/trafficLogging/maxMetadataHours'),
        this.registryStore.get('system/trafficLogging/fullHours'),
        this.registryStore.get('system/trafficLogging/maxFullHours'),
      ])

      Object.assign(this.ceilings, {
        defaultLevel: defaultLevel ?? undefined,
        metadataHours: toNumber(metadataHours),
        maxMetadataHours: toNumber(maxMetadataHours),
        fullHours: toNumber(fullHours),
        maxFullHours: toNumber(maxFullHours),
      })
    } catch (e) {
      console.warn('Traffic logging ceilings unavailable; falling back to server validation.', e)
    }
  }

  /**
   * Writes one resource's changed fields.
   *
   * Only the fields present in `patch` are sent: an absent field is left unchanged by the API, and an
   * explicit null clears the override. Sending the whole row would promote the deployment defaults
   * for untouched fields into explicit overrides.
   */
  async save(
    resourceId: string,
    family: ResourceFamily,
    patch: ResourceLogging,
  ): Promise<void> {
    const service = family === 'ingress' ? this.ingressesService : this.egressesService
    await service.updateLogging(resourceId, patch)

    const row = this.rows.find((r) => r.resourceId === resourceId && r.family === family)
    if (row) {
      Object.assign(row, patch)
    }

    // The cached list now disagrees with the server about this resource's override.
    this.resourcesStore.invalidate()
  }

  setSaving(value: boolean): void {
    this.workState.isSaving = value
  }

}

function hasOverride(row: LoggingRow): boolean {
  return row.logLevel != null || row.logRetentionDays != null || row.logFullRetentionDays != null
}

function toNumber(value: any): number | undefined {
  if (value == null) return undefined
  const n = Number(value)
  return Number.isFinite(n) ? n : undefined
}
